import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SeoHead } from "../components/SeoHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsCard from "../components/NewsCard";
import VideoCard from "../components/VideoCard";
import PodCard from "../components/PodCard";
import AdSlot from "../components/AdSlot";
import BriefModal from "../components/BriefModal";
import ShareModal from "../components/ShareModal";
import DailyBriefModal from "../components/DailyBriefModal";
import SponsorModal from "../components/SponsorModal";
import MarketsBoard from "../components/MarketsBoard";
import WatchlistPanel from "../components/WatchlistPanel";
import { fetchNews, fetchVideos, fetchPodcasts, fetchVoices, fetchEditorial, fetchInternational } from "../lib/api";

const LABELS = {
  top: "Front Page",
  government: "Politics",
  economy: "Business",
  markets: "Markets",
  money: "Make Money",
  world: "World",
  technology: "Technology",
  ai: "AI · Artificial Intelligence",
  health: "Health",
  medicine: "Medicine",
  realestate: "Real Estate",
  family: "Family",
  sports: "Sports",
  hollywood: "Hollywood",
  entertainment: "Entertainment",
  culture: "Culture",
  videos: "Video",
  podcasts: "Investigations",
  voices: "Voices · Curated Commentary",
  editorial: "Editorial · Opinion",
};

// Category fallback map — when a primary category has too few articles, pull
// from these sibling/related categories so subject sections always populate.
// Mirrors the keyword-derivation logic already used on the home page.
const FALLBACK_CATEGORIES = {
  money: ["economy", "markets"],
  ai: ["technology"],
  culture: ["entertainment", "hollywood"],
  lifestyle: ["entertainment", "hollywood", "family"],
  foreign: ["world"],
  science: ["technology", "health"],
  space: ["technology", "science"],
  realestate: ["economy", "markets"],
  family: ["entertainment", "culture"],
  hollywood: ["entertainment"],
};

const KIND_TAGLINE = {
  voices: "Independent columnists you won't find on big networks — Bill O'Reilly, Bari Weiss, Matt Taibbi, Andrew Sullivan, Glenn Greenwald, and more. Clearly labeled as commentary.",
  editorial: "Newseze editorial board notes + clearly-labeled opinion. Kept separate from the news.",
  world: "Independent newsrooms outside the U.S. America is our home page — world is opt-in.",
  money: "Live markets · stocks · commodities · crypto · personal finance · side hustles · careers · real estate. Build wealth on your terms.",
  markets: "Live tickers, mega-cap movers, commodities, treasuries, FX, crypto — refreshed every 60 seconds.",
  ai: "Frontier AI news from primary sources: Anthropic, OpenAI, Google AI, MIT Tech Review, Stratechery, Hugging Face — plus practical AI tools coverage.",
  hollywood: "Variety · Hollywood Reporter · Deadline · IndieWire. Film, TV, streaming, deals, box office.",
  entertainment: "Music · Concerts · Awards · Pop culture. Rolling Stone, Billboard, Vulture, EW.",
  culture: "Books · ideas · museums · long reads. The Atlantic, Smithsonian, NYT Books, Nat Geo.",
  sports: "Massive coverage — NFL · NBA · MLB · NHL · College · Soccer · F1 · Tennis · MMA · Boxing. ESPN, CBS, Athletic, Bleacher Report.",
  health: "Trusted public-health reporting from STAT News + WHO outbreak alerts.",
  medicine: "Drug approvals, clinical research, treatments. NIH MedlinePlus, Medical News Today, Healthline, Drugs.com.",
  realestate: "Housing market, mortgages, commercial real estate. Realtor.com, Zillow Research, HousingWire, Bisnow, Curbed.",
  family: "Parenting, home, lifestyle, family finance. Today Parents, Fatherly, Real Simple, Parents.com.",
};

export default function Category() {
  const { cat } = useParams();
  const key = cat || "top";
  const [items, setItems] = useState([]);
  const [brief, setBrief] = useState(null);
  const [share, setShare] = useState(null);
  const [showBriefSub, setShowBriefSub] = useState(false);
  const [showSponsor, setShowSponsor] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const load = async () => {
      try {
        if (key === "videos") {
          const v = await fetchVideos({ limit: 24 });
          if (alive) setItems(v);
        } else if (key === "podcasts") {
          const p = await fetchPodcasts();
          if (alive) setItems(p);
        } else if (key === "voices") {
          const n = await fetchVoices(40);
          if (alive) setItems(n);
        } else if (key === "editorial") {
          const n = await fetchEditorial(40);
          if (alive) setItems(n);
        } else if (key === "world") {
          const n = await fetchInternational();
          if (alive) setItems(n);
        } else {
          // Primary category fetch; if sparse, broaden via sibling-category
          // fallbacks so subject pages never feel empty.
          const primary = await fetchNews({ category: key, limit: 30 });
          let merged = primary;
          if (primary.length < 6 && FALLBACK_CATEGORIES[key]) {
            const seen = new Set(primary.map((a) => a.id));
            for (const sib of FALLBACK_CATEGORIES[key]) {
              try {
                const more = await fetchNews({ category: sib, limit: 24 });
                for (const a of more) {
                  if (!seen.has(a.id)) { seen.add(a.id); merged.push(a); }
                  if (merged.length >= 30) break;
                }
              } catch {}
              if (merged.length >= 30) break;
            }
          }
          if (alive) setItems(merged);
        }
      } catch (e) { console.error(e); }
      finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [key]);

  const label = LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);

  return (
    <>
      <SeoHead
        title={`${label} News & Headlines`}
        description={KIND_TAGLINE[key] || `The latest balanced, trust-scored ${label} coverage on Newseze. No drama. No conspiracies.`}
      />
      <Header ticker={[]} onOpenBrief={() => setShowBriefSub(true)} />

      <main className="max-w-[1280px] mx-auto px-5 py-6">
        <div className="section-title mb-3">
          <span className="pip" />
          <h2 data-testid="cat-title">{label}</h2>
          {(key === "voices" || key === "editorial") && (
            <span className="ml-auto text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] bg-[#fff7d6] px-2 py-1 rounded" data-testid="opinion-badge">
              Commentary — not news
            </span>
          )}
        </div>
        {KIND_TAGLINE[key] && (
          <p className="text-[13.5px] text-[var(--muted)] max-w-[820px] mb-6 leading-snug" data-testid="cat-tagline">{KIND_TAGLINE[key]}</p>
        )}

        {(key === "money" || key === "markets") && (
          <div className="mb-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="markets-section">
            <MarketsBoard />
            <WatchlistPanel onOpenBrief={() => setShowBriefSub(true)} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div>
            {loading ? (
              <div className="text-[14px] text-[var(--muted)] italic py-10 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-center py-16" data-testid="cat-empty">
                <div className="text-[15px] font-bold text-[var(--ink)] mb-2">No {label} stories in the wire right now.</div>
                <p className="text-[13px] text-[var(--muted)] max-w-[480px] mx-auto leading-snug">
                  Newseze refreshes hourly — your next batch is on the way. In the meantime, browse
                  <a href="/" className="text-[var(--blue)] font-black hover:underline mx-1">Top Stories</a>
                  or
                  <a href="/news" className="text-[var(--blue)] font-black hover:underline mx-1">Breaking</a>.
                </p>
              </div>
            ) : key === "videos" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((v) => <VideoCard key={v.id} video={v} />)}
              </div>
            ) : key === "podcasts" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((p) => <PodCard key={p.id} pod={p} />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.from(new Map(items.map((a) => [a.id, a])).values()).map((a) => (
                  <NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <AdSlot size="h-rect" slot={`category-${key}-top`} />
            <div className="sponsor-cta">
              <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Sponsor this section</div>
              <h4 className="text-[18px] font-black mt-1 leading-tight">{label} sponsor slot.</h4>
              <p className="text-[12.5px] opacity-85 mt-1.5">Reach readers consuming trust-scored {label} coverage.</p>
              <button onClick={() => setShowSponsor(true)} className="btn gold mt-3 w-full justify-center" data-testid="cat-sponsor-cta">
                Get media kit
              </button>
            </div>
            <AdSlot size="sky" slot={`category-${key}-sky`} />
          </aside>
        </div>
      </main>

      <Footer onOpenBrief={() => setShowBriefSub(true)} onOpenSponsor={() => setShowSponsor(true)} />

      {brief && <BriefModal article={brief} onClose={() => setBrief(null)} />}
      {share && <ShareModal article={share} onClose={() => setShare(null)} />}
      {showBriefSub && <DailyBriefModal onClose={() => setShowBriefSub(false)} />}
      {showSponsor && <SponsorModal onClose={() => setShowSponsor(false)} />}
    </>
  );
}
